/**
 * The fonts a client may choose for their portal.
 *
 * A fixed list rather than a free-text CSS stack, for two reasons. A typed
 * stack is silently wrong more often than not — a name the visitor does not
 * have installed simply falls back, so the client sees their choice ignored
 * with no explanation. And a font only renders if something loads it: the
 * portal ships no web fonts, so anything beyond the faces already on the
 * machine has to be fetched deliberately.
 *
 * Entries are therefore one of two kinds:
 *
 * - **System stacks** — already present on the visitor's machine. Nothing is
 *   fetched, nothing can fail, and they render instantly.
 * - **Web fonts** — fetched from Google Fonts the first time a portal asks for
 *   one, via `ensureBrandFont`. Chosen for breadth of weights and for reading
 *   well at the small sizes this UI uses.
 */

/** One selectable font. */
export interface BrandFont {
  /** The CSS `font-family` stack stored on the brand. */
  readonly value: string;
  /** What the client sees in the picker. */
  readonly label: string;
  /**
   * Google Fonts family name, when this face has to be fetched. Absent for a
   * system stack, which needs no loading.
   */
  readonly google?: string;
}

/** The fonts on offer, system stacks first so the safe options lead. */
export const BRAND_FONTS: readonly BrandFont[] = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial (system)' },
  {
    value:
      'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    label: 'System default',
  },
  { value: '"Helvetica Neue", Helvetica, Arial, sans-serif', label: 'Helvetica Neue (system)' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana (system)' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma (system)' },
  { value: '"Trebuchet MS", Helvetica, sans-serif', label: 'Trebuchet MS (system)' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia (system, serif)' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman (system, serif)' },

  { value: 'Inter, Arial, sans-serif', label: 'Inter', google: 'Inter' },
  { value: 'Roboto, Arial, sans-serif', label: 'Roboto', google: 'Roboto' },
  { value: '"Open Sans", Arial, sans-serif', label: 'Open Sans', google: 'Open Sans' },
  { value: 'Lato, Arial, sans-serif', label: 'Lato', google: 'Lato' },
  { value: 'Montserrat, Arial, sans-serif', label: 'Montserrat', google: 'Montserrat' },
  { value: '"Source Sans 3", Arial, sans-serif', label: 'Source Sans 3', google: 'Source Sans 3' },
  { value: 'Poppins, Arial, sans-serif', label: 'Poppins', google: 'Poppins' },
  { value: '"Nunito Sans", Arial, sans-serif', label: 'Nunito Sans', google: 'Nunito Sans' },
  { value: '"Work Sans", Arial, sans-serif', label: 'Work Sans', google: 'Work Sans' },
  { value: '"IBM Plex Sans", Arial, sans-serif', label: 'IBM Plex Sans', google: 'IBM Plex Sans' },
  {
    value: 'Merriweather, Georgia, serif',
    label: 'Merriweather (serif)',
    google: 'Merriweather',
  },
  {
    value: '"Playfair Display", Georgia, serif',
    label: 'Playfair Display (serif)',
    google: 'Playfair Display',
  },
];

/** The default when a client has chosen nothing. */
export const DEFAULT_BRAND_FONT = 'Arial, Helvetica, sans-serif';

/**
 * Finds the catalogue entry for a stored stack.
 *
 * @param value - The stored `font-family` stack.
 * @returns The entry, or undefined for a stack chosen before this list existed.
 */
export function findBrandFont(value: string | null | undefined): BrandFont | undefined {
  if (!value) return undefined;
  return BRAND_FONTS.find((font) => font.value === value);
}

/** Families already requested, so a re-render never adds a second link. */
const loaded = new Set<string>();

/**
 * Loads the web font a stack needs, if it needs one.
 *
 * Idempotent and safe to call on every brand application: a system stack
 * returns immediately, and a family already requested is not requested again.
 * Only the weights this UI actually uses are fetched — 400 for body, 700 and
 * 800 for the headings and table labels — because a full family is a large
 * download for a difference nobody sees.
 *
 * @param value - The stored `font-family` stack.
 */
export function ensureBrandFont(value: string | null | undefined): void {
  if (typeof document === 'undefined') return;

  const font = findBrandFont(value);
  // A system stack, an unknown stack, or nothing chosen: there is nothing to
  // fetch, and the browser resolves it against what is installed.
  if (!font?.google || loaded.has(font.google)) return;

  loaded.add(font.google);

  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    font.google,
  ).replace(/%20/g, '+')}:wght@400;700;800&display=swap`;

  // `display=swap`: text paints in the fallback immediately and re-renders when
  // the face arrives, rather than leaving the portal blank while it loads.
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}
